import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { toast } from 'sonner';
import {
  Inbox, Send, FileText, Trash2, AlertOctagon, Archive, Plus, PenSquare,
  Search as SearchIcon, X, Clock, Star, ChevronRight, ChevronDown,
  Folder, FolderOpen, Reply, Settings as SettingsIcon,
  MoreHorizontal, FolderPlus, Pencil, Palette, FolderInput, CalendarDays, Video,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { destinoTransito, esTransito } from '../../lib/transito';
import { urlEnMeet } from '../../lib/dominios';
import { badgeRecibidos } from '../../lib/carpetaCorreo';

const FOLDERS = [
  { id: 'inbox', label: 'Recibidos', icon: Inbox },
  { id: 'needs_reply', label: 'Por responder', icon: Reply, virtual: true },
  { id: 'starred', label: 'Destacados', icon: Star, virtual: true },
  /* Programados y pospuestos comparten vista con pestañas: una sola fila aquí
     y el resto del alto se lo quedan las carpetas. */
  { id: 'transito', label: 'En espera', icon: Clock, virtual: true, hint: 'Programados y pospuestos' },
  { id: 'sent', label: 'Enviados', icon: Send },
  { id: 'drafts', label: 'Borradores', icon: FileText },
  /* "Todos" es TODO el correo (recibidos + archivado + enviados), no la carpeta
     Archivo: casi ningún buzón de Hostinger la tiene y la fila salía vacía. */
  { id: 'all', label: 'Todos', icon: Archive },
  { id: 'spam', label: 'Spam', icon: AlertOctagon },
  { id: 'trash', label: 'Papelera', icon: Trash2 },
];

/** Colores de carpeta. Sirven para reconocerla de un vistazo, no para agrupar. */
const COLORES_CARPETA = [
  '#2563eb', '#3f9576', '#b07d2e', '#4b7bc0',
  '#b5485d', '#2f8f9d', '#8a5cc0', '#6b7280',
];
// Lo que oye un lector de pantalla en vez de «Color #2563eb» (M28)
const NOMBRE_COLOR = {
  '#2563eb': 'Azul', '#3f9576': 'Verde', '#b07d2e': 'Ámbar', '#4b7bc0': 'Azul grisáceo',
  '#b5485d': 'Rojo', '#2f8f9d': 'Turquesa', '#8a5cc0': 'Morado', '#6b7280': 'Gris',
};

const LOGICAL_RAW = new Set([
  'inbox', 'sent', 'drafts', 'trash', 'spam', 'junk', 'archive',
  'sent items', 'deleted items', 'junk e-mail',
]);

function isSystemLike(node) {
  const logical = (node.logical || '').toLowerCase();
  if (logical && FOLDERS.some((f) => f.id === logical && !f.virtual)) return true;
  const name = (node.name || node.label || '').toLowerCase();
  if (LOGICAL_RAW.has(name)) return true;
  if (name === 'inbox') return true;
  return false;
}

/** Flatten tree → rows with depth (avoids recursive JSX / babel stack overflow). */
function flattenTree(nodes, depth = 0, out = []) {
  for (const n of nodes || []) {
    const kids = n.children || [];
    out.push({
      id: n.name || n.id,
      label: n.label || n.name,
      depth,
      selectable: n.selectable !== false,
      hasKids: kids.length > 0 || !!n.has_children,
    });
    if (kids.length) flattenTree(kids, depth + 1, out);
  }
  return out;
}

function pruneSystem(nodes) {
  const out = [];
  for (const n of nodes || []) {
    const kids = pruneSystem(n.children || []);
    if (!isSystemLike(n)) {
      out.push({ ...n, children: kids });
    } else if (kids.length) {
      out.push(...kids);
    }
  }
  return out;
}

export default function MailSidebar({ accounts, onNavigate }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const {
    accountFilter, setAccountFilter, activeFolder, setActiveFolder, openComposer, setListSearch,
  } = useStore();
  const [imapOpen, setImapOpen] = useState(true);
  /* Diálogo único para crear / renombrar. null | {modo:'crear'|'renombrar',
     accountId, parent, folder, nombre, busy} */
  const [dlg, setDlg] = useState(null);
  const [borrar, setBorrar] = useState(null);   // null | {accountId, folder, label, busy}
  /* Qué ramas del árbol están abiertas. Se recuerda entre sesiones: quien tiene
     Clientes/2026/Facturas no quiere volver a abrirlo tres veces cada mañana. */
  const [expanded, setExpanded] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('zuno_carpetas_abiertas') || '[]')); }
    catch (_) { return new Set(); }
  });

  /* Key propia (no 'unified'): la lista usa una infinite query con otra forma de caché.
     Se pide UN mensaje porque lo único que interesa es el contador, y el del
     servidor cuenta la carpeta entera: antes se contaban los 80 cargados, así
     que con más correo sin leer la barra lateral se quedaba corta para siempre. */
  const { data: inbox } = useQuery({
    queryKey: ['unified-badge', accountFilter],
    queryFn: async () => (await api.get('/mail/unified?folder=inbox&limit=1'
      + (accountFilter === 'all' ? '' : `&account_id=${encodeURIComponent(accountFilter)}`))).data,
    enabled: (accounts?.length || 0) > 0,
    refetchInterval: 60000,
  });
  // Bug 3: la suma de las pestañas de Recibidos (antes, toda la bandeja: no cuadraba con lo visible)
  const unread = badgeRecibidos(inbox);

  const { data: saved = [] } = useQuery({ queryKey: ['saved-searches'], queryFn: async () => (await api.get('/saved-searches')).data });
  /* Color por carpeta, guardado en el servidor: la misma carpeta se ve del
     mismo color en cualquier equipo. Clave "cuenta:carpeta". */
  const { data: coloresCarpeta = {} } = useQuery({
    queryKey: ['folder-prefs'],
    queryFn: async () => (await api.get('/mail/folders/prefs')).data,
    staleTime: 300000,
  });
  /* Clave propia para el contador: la LISTA usa una consulta infinita con la
     misma ruta, y compartir clave hacía que una recibiera los datos con la
     forma de la otra —la pantalla se quedaba en blanco al abrir Programados—. */
  const { data: snoozedList = [] } = useQuery({
    queryKey: ['snoozed-count'],
    queryFn: async () => (await api.get('/mail/snoozed')).data,
    enabled: (accounts?.length || 0) > 0,
    refetchInterval: 60000,
  });
  const snoozeCount = Array.isArray(snoozedList) ? snoozedList.length : 0;

  const { data: scheduledList = [] } = useQuery({
    queryKey: ['scheduled-count'],
    queryFn: async () => (await api.get('/scheduled')).data,
    enabled: (accounts?.length || 0) > 0,
    refetchInterval: 30000,
  });
  const scheduledPending = (Array.isArray(scheduledList) ? scheduledList : [])
    .filter((s) => ['scheduled', 'retry', 'sending'].includes(s.status)).length;

  const folderAccountId = useMemo(() => {
    if (accountFilter && accountFilter !== 'all') return accountFilter;
    const def = (accounts || []).find((a) => a.is_default) || (accounts || [])[0];
    return def?.id || null;
  }, [accountFilter, accounts]);

  /* Con el filtro en "Todas" y más de un buzón se piden los árboles de TODOS.
     Antes solo salían las carpetas de la cuenta por defecto, así que las
     carpetas propias del segundo buzón sencillamente no existían en la barra
     lateral: había que cambiar el filtro para verlas. */
  const verTodas = accountFilter === 'all' && (accounts?.length || 0) > 1;

  const { data: folderData } = useQuery({
    queryKey: ['mail-folders', folderAccountId],
    queryFn: async () => (await api.get(`/mail/folders?account_id=${folderAccountId}`)).data,
    enabled: !!folderAccountId && !verTodas,
    staleTime: 120000,
  });

  const { data: folderDataAll } = useQuery({
    queryKey: ['mail-folders-all'],
    queryFn: async () => (await api.get('/mail/folders/all')).data,
    enabled: verTodas,
    staleTime: 120000,
  });

  /* No leídos por carpeta REAL. Cuesta un STATUS por carpeta contra el
     proveedor, así que el backend lo cachea 3 min y aquí se refresca despacio:
     saber que hay correo sin abrir no vale una petición cada pocos segundos. */
  const cuentasDeCarpetas = useMemo(() => {
    if (verTodas) return (folderDataAll?.accounts || []).map((a) => a.account_id).filter(Boolean);
    return folderAccountId ? [folderAccountId] : [];
  }, [verTodas, folderDataAll, folderAccountId]);

  const { data: unreadByAccount = {} } = useQuery({
    queryKey: ['folders-unread', cuentasDeCarpetas.join(',')],
    queryFn: async () => {
      const pares = await Promise.all(cuentasDeCarpetas.map(async (id) => {
        try {
          const r = (await api.get(`/mail/folders/unread?account_id=${id}`)).data;
          return [id, r.folders || {}];
        } catch (_) { return [id, {}]; }
      }));
      return Object.fromEntries(pares);
    },
    enabled: cuentasDeCarpetas.length > 0,
    staleTime: 120000,
    refetchInterval: 180000,
  });

  /* Grupos de carpetas: uno por cuenta cuando se ven todas, uno solo si no.
     Cada fila sabe de qué cuenta es para poder abrirla sin ambigüedad. */
  const gruposCarpetas = useMemo(() => {
    const armar = (datos, accountId, email) => {
      const filas = flattenTree(pruneSystem(datos?.tree || []));
      const delim = datos?.delimiter || '.';
      // Respeta expandir/colapsar: una fila se oculta si algún padre está cerrado.
      const visibles = filas.filter((row) => {
        if (row.depth === 0) return true;
        const parts = row.id.split(delim);
        let parentPath = '';
        for (let i = 0; i < parts.length - 1; i++) {
          parentPath = parentPath ? parentPath + delim + parts[i] : parts[i];
          const parent = filas.find((f) => f.id === parentPath);
          if (parent && parent.hasKids && !expanded.has(parent.id)) return false;
        }
        return true;
      });
      const noLeidos = unreadByAccount[accountId] || {};
      return {
        accountId,
        email,
        rows: visibles.map((r) => ({ ...r, unread: (noLeidos[r.id] || {}).unread || 0 })),
      };
    };
    if (verTodas) {
      return (folderDataAll?.accounts || [])
        .map((a) => armar(a, a.account_id, a.account_email))
        .filter((g) => g.rows.length > 0);
    }
    if (!folderAccountId) return [];
    const g = armar(folderData, folderAccountId,
                    (accounts || []).find((a) => a.id === folderAccountId)?.email);
    return g.rows.length > 0 ? [g] : [];
  }, [verTodas, folderDataAll, folderData, folderAccountId, accounts, expanded, unreadByAccount]);

  const hayCarpetas = gruposCarpetas.some((g) => g.rows.length > 0);

  /* Abierta como menú desde el Calendario (AppShell): elegir carpeta, búsqueda o Redactar
     tiene que llevar al correo. En el correo no hace nada. */
  const alCorreo = () => { if (!loc.pathname.startsWith('/mail')) nav('/mail'); };

  const selectFolder = (id) => {
    setActiveFolder(id);
    alCorreo();
    onNavigate && onNavigate();
  };

  const toggleNode = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      try { localStorage.setItem('zuno_carpetas_abiertas', JSON.stringify([...n])); } catch (_) {}
      return n;
    });
  };

  /* --- Carpetas: crear, renombrar, colorear, eliminar ---
     Son carpetas IMAP de verdad, así que lo que se haga aquí se ve igual en el
     móvil y en cualquier otro cliente de correo. */
  const refrescarCarpetas = () => {
    qc.invalidateQueries({ queryKey: ['mail-folders'] });
    qc.invalidateQueries({ queryKey: ['mail-folders-all'] });
    qc.invalidateQueries({ queryKey: ['folders-unread'] });
    qc.invalidateQueries({ queryKey: ['folder-prefs'] });
  };

  const abrirCrear = (accountId, parent = null, etiquetaPadre = '') =>
    setDlg({ modo: 'crear', accountId, parent, etiquetaPadre, nombre: '', busy: false });

  /* El tutorial termina con "Crear mi primera carpeta" y avisa por aquí: así el
     que acaba de leerlo no tiene que salir a buscar dónde se hacía. */
  useEffect(() => {
    const alPedir = () => { if (folderAccountId) abrirCrear(folderAccountId); };
    window.addEventListener('orvian:nueva-carpeta', alPedir);
    return () => window.removeEventListener('orvian:nueva-carpeta', alPedir);
  }, [folderAccountId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const abrirRenombrar = (accountId, folder, nombre) =>
    setDlg({ modo: 'renombrar', accountId, folder, nombre, busy: false });

  const guardarCarpeta = async () => {
    const d = dlg;
    if (!d || !d.nombre.trim()) return;
    setDlg((x) => ({ ...x, busy: true }));
    try {
      if (d.modo === 'crear') {
        await api.post('/mail/folders', {
          account_id: d.accountId, name: d.nombre.trim(), parent: d.parent || null,
        });
        toast.success(d.parent ? 'Subcarpeta creada' : 'Carpeta creada');
      } else {
        await api.patch('/mail/folders', {
          account_id: d.accountId, folder: d.folder, name: d.nombre.trim(),
        });
        toast.success('Carpeta renombrada');
      }
      refrescarCarpetas();
      setDlg(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
      setDlg((x) => ({ ...x, busy: false }));
    }
  };

  const eliminarCarpeta = async () => {
    const b = borrar;
    if (!b) return;
    setBorrar((x) => ({ ...x, busy: true }));
    try {
      const r = (await api.delete(
        `/mail/folders?account_id=${encodeURIComponent(b.accountId)}&folder=${encodeURIComponent(b.folder)}`,
      )).data;
      toast.success(r.rescatados > 0
        ? `Carpeta eliminada · ${r.rescatados} correo(s) puestos a salvo en ${r.destino_legible}`
        : 'Carpeta eliminada');
      if (activeFolder === b.folder) setActiveFolder('inbox');
      refrescarCarpetas();
      qc.invalidateQueries({ queryKey: ['unified'] });
      setBorrar(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
      setBorrar((x) => ({ ...x, busy: false }));
    }
  };

  const pintarCarpeta = async (accountId, folder, color) => {
    try {
      await api.put('/mail/folders/prefs', { account_id: accountId, folder, color });
      qc.invalidateQueries({ queryKey: ['folder-prefs'] });
    } catch { toast.error('No se pudo cambiar el color'); }
  };

  const applySavedSearch = (s) => {
    setListSearch?.(s.query || s.name || '');
    setActiveFolder('inbox');
    alCorreo();
    onNavigate && onNavigate();
  };

  const onSettings = loc.pathname.startsWith('/settings');

  const countFor = (id) => {
    if (id === 'inbox') return unread;
    if (id === 'transito') return snoozeCount + scheduledPending;
    return 0;
  };

  return (
    <div className="gmail-sidebar h-full" data-testid="mail-sidebar-col">
      {/* Redactar */}
      <div style={{ padding: '14px 12px 10px' }}>
        <button
          type="button"
          className="gmail-compose"
          data-testid="mail-compose-button"
          title="Redactar"
          onClick={() => { openComposer('new'); alCorreo(); onNavigate && onNavigate(); }}
        >
          <PenSquare className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} />
          <span className="od-side-text">Redactar</span>
        </button>
      </div>

      <div className="od-scroll flex-1 min-h-0" style={{ padding: '2px 8px 12px' }}>
        {/* Navegación principal */}
        {FOLDERS.map((f) => {
          const Icon = f.icon;
          const showActive = f.id === 'transito'
            ? esTransito(activeFolder)
            : activeFolder === f.id;
          const count = countFor(f.id);
          return (
            <button
              key={f.id}
              type="button"
              data-testid={`folder-${f.id}`}
              title={f.hint || f.label}
              onClick={() => selectFolder(
                f.id === 'transito'
                  ? destinoTransito({ programados: scheduledPending, pospuestos: snoozeCount })
                  : f.id,
              )}
              className={`gmail-folder ${showActive ? 'gmail-folder-active' : ''}`}
              aria-current={showActive ? 'page' : undefined}   // B10: la activa se anunciaba solo con color
            >
              <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={showActive ? 2.15 : 1.75} />
              <span className="od-side-text truncate flex-1 text-left">{f.label}</span>
              {count > 0 && <span className="gmail-folder-count">{count}</span>}
            </button>
          );
        })}

        {/* Calendario: vive aquí, con el correo, en vez de un botón más en la barra superior */}
        <button
          type="button"
          data-testid="sidebar-calendar"
          title="Calendario"
          onClick={() => { nav('/calendar'); onNavigate && onNavigate(); }}
          className="gmail-folder"
          style={{ marginTop: 6 }}
        >
          <CalendarDays className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
          <span className="od-side-text truncate flex-1 text-left">Calendario</span>
        </button>

        {/* Orvian Meet: videollamadas en meet.orvian.mx, en otra pestaña (ver claude/meet.md) */}
        <button
          type="button"
          data-testid="sidebar-meet"
          title="Orvian Meet: videollamadas (se abre en otra pestaña)"
          onClick={() => { window.open(urlEnMeet('/'), '_blank', 'noopener'); onNavigate && onNavigate(); }}
          className="gmail-folder"
        >
          <Video className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
          <span className="od-side-text truncate flex-1 text-left">Meet</span>
        </button>

        {/* ---------- Carpetas propias ----------
            Son carpetas IMAP reales: lo que se crea aquí existe también en el
            móvil y en cualquier otro cliente. La sección se enseña SIEMPRE que
            haya una cuenta, aunque no haya ninguna carpeta todavía: si solo
            apareciera cuando ya existen, nadie encontraría dónde crear la
            primera. */}
        {(accounts?.length || 0) > 0 && (
          <div className="od-side-section">
            <div className="gmail-section-label flex items-center justify-between pr-1">
              <button
                type="button"
                className="flex items-center gap-1.5 flex-1 min-w-0 bg-transparent border-0 p-0 cursor-pointer text-inherit font-inherit uppercase tracking-inherit hover:opacity-80"
                onClick={() => setImapOpen((v) => !v)}
                data-testid="imap-folders-toggle"
              >
                <span>Carpetas</span>
                {imapOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
              {folderAccountId && (
                <button
                  type="button"
                  className="od-icon-btn-sm"
                  onClick={() => abrirCrear(verTodas ? (gruposCarpetas[0]?.accountId || folderAccountId) : folderAccountId)}
                  data-testid="folder-create-button"
                  aria-label="Nueva carpeta"
                  title="Nueva carpeta"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
            {imapOpen && (
              <div data-testid="imap-folder-tree">
                {gruposCarpetas.map((grupo) => (
                  <div key={grupo.accountId}>
                    {gruposCarpetas.length > 1 && (
                      <div className="px-3 pt-1.5 pb-1 text-[10px] truncate" style={{ color: 'var(--text-3)' }}>
                        {grupo.email}
                      </div>
                    )}
                    {grupo.rows.map((row) => {
                      const isOpen = expanded.has(row.id);
                      const active = activeFolder === row.id;
                      const color = coloresCarpeta[`${grupo.accountId}:${row.id}`];
                      const Icono = isOpen && row.hasKids ? FolderOpen : Folder;
                      return (
                        <div
                          key={`${grupo.accountId}:${row.id}`}
                          className={`gmail-folder gmail-folder-imap group ${active ? 'gmail-folder-active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                          style={{ paddingLeft: 12 + row.depth * 12, paddingRight: 4 }}
                        >
                          {/* Abrir la rama es un objetivo aparte del de abrir la
                              carpeta: antes un clic hacía las dos cosas y no
                              había forma de desplegar sin cambiar de vista. */}
                          {row.hasKids ? (
                            <button
                              type="button"
                              className="od-icon-btn-sm !w-4 !h-4 shrink-0 opacity-70"
                              onClick={(e) => { e.stopPropagation(); toggleNode(row.id); }}
                              aria-label={isOpen ? 'Contraer' : 'Desplegar'}
                              data-testid={`imap-folder-toggle-${row.id}`}
                            >
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          ) : (
                            <span className="w-4 shrink-0" />
                          )}
                          <button
                            type="button"
                            data-testid={`imap-folder-${row.id}`}
                            title={row.unread > 0 ? `${row.label} — ${row.unread} sin leer` : row.label}
                            onClick={() => {
                              if (!row.selectable) { toggleNode(row.id); return; }
                              /* Una carpeta propia solo existe en SU buzón. Si se
                                 abre desde la vista de todas, hay que fijar antes
                                 el filtro a esa cuenta: si no, el otro buzón no
                                 sabe resolver ese nombre y acaba enseñando su
                                 bandeja de entrada como si fuera la carpeta. */
                              if (gruposCarpetas.length > 1 && accountFilter !== grupo.accountId) {
                                setAccountFilter(grupo.accountId);
                              }
                              selectFolder(row.id);
                            }}
                            className="flex items-center gap-2.5 flex-1 min-w-0 text-left bg-transparent border-0 cursor-pointer p-0"
                          >
                            <Icono
                              className="h-4 w-4 shrink-0"
                              strokeWidth={1.75}
                              style={{ color: color || 'var(--text-3)' }}
                              fill={color ? color : 'none'}
                              fillOpacity={color ? 0.16 : 0}
                            />
                            <span className={`truncate flex-1 text-[13px] ${row.unread > 0 ? 'font-semibold' : ''}`}>{row.label}</span>
                          </button>
                          {row.unread > 0 && <span className="gmail-folder-count shrink-0">{row.unread}</span>}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="od-icon-btn-sm !w-6 !h-6 shrink-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Opciones de ${row.label}`}
                                data-testid={`folder-menu-${row.id}`}
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="rounded-xl w-56">
                              <DropdownMenuItem onClick={() => abrirCrear(grupo.accountId, row.id, row.label)}>
                                <FolderPlus className="h-4 w-4 mr-2" />Nueva subcarpeta
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => abrirRenombrar(grupo.accountId, row.id, row.label)}>
                                <Pencil className="h-4 w-4 mr-2" />Renombrar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <div className="px-2 py-1.5">
                                <div className="text-[11px] mb-1.5 flex items-center gap-1.5" style={{ color: 'var(--text-3)' }}>
                                  <Palette className="h-3 w-3" />Color
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {COLORES_CARPETA.map((c) => (
                                    <button
                                      key={c}
                                      type="button"
                                      onClick={() => pintarCarpeta(grupo.accountId, row.id, c)}
                                      className="rounded-full border-0 cursor-pointer"
                                      style={{
                                        width: 18, height: 18, background: c,
                                        outline: color === c ? '2px solid var(--text)' : 'none',
                                        outlineOffset: 1,
                                      }}
                                      aria-label={`Color ${NOMBRE_COLOR[c] || c}`}
                                      aria-pressed={color === c}
                                    />
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => pintarCarpeta(grupo.accountId, row.id, null)}
                                    className="rounded-full cursor-pointer grid place-items-center"
                                    style={{ width: 18, height: 18, border: '1px dashed var(--line)', background: 'transparent' }}
                                    aria-label="Sin color"
                                  >
                                    <X className="h-2.5 w-2.5" style={{ color: 'var(--text-3)' }} />
                                  </button>
                                </div>
                              </div>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setBorrar({ accountId: grupo.accountId, folder: row.id, label: row.label, busy: false })}
                                style={{ color: 'var(--danger)' }}
                                data-testid={`folder-delete-${row.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />Eliminar carpeta
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      );
                    })}
                    {gruposCarpetas.length > 1 && (
                      <button
                        type="button"
                        className="gmail-folder gmail-folder-imap"
                        style={{ paddingLeft: 12, color: 'var(--text-3)', fontSize: 12 }}
                        onClick={() => abrirCrear(grupo.accountId)}
                        data-testid={`folder-new-${grupo.accountId}`}
                      >
                        <span className="w-4 shrink-0" />
                        <Plus className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">Nueva carpeta</span>
                      </button>
                    )}
                  </div>
                ))}
                {!hayCarpetas && (
                  <div className="px-3 py-2 text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
                    Aún no tienes carpetas. Crea una para guardar por tema —
                    clientes, facturas, un proyecto— y podrás hacer que el correo
                    nuevo entre solo.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Búsquedas guardadas */}
        {saved.length > 0 && (
          <div className="od-side-section">
            <div className="gmail-section-label">Búsquedas</div>
            <div>
              {saved.map((s) => (
                <div key={s.id} className="gmail-folder group" style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  <button
                    type="button"
                    className="flex items-center gap-3 flex-1 min-w-0 text-left bg-transparent border-0 cursor-pointer p-0"
                    onClick={() => applySavedSearch(s)}
                    data-testid={`saved-search-${s.id}`}
                  >
                    <SearchIcon className="h-4 w-4 shrink-0" style={{ color: 'var(--text-3)' }} />
                    <span className="truncate flex-1">{s.name}</span>
                  </button>
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 od-icon-btn-sm !w-6 !h-6"
                    onClick={async () => {
                      await api.delete(`/saved-searches/${s.id}`);
                      qc.invalidateQueries({ queryKey: ['saved-searches'] });
                    }}
                    aria-label="Eliminar búsqueda"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pie fijo: Ajustes (mismo nombre en todo el producto, M19) */}
      <div className="flex-none" style={{ borderTop: '1px solid var(--line)', padding: 8 }}>
        <button
          type="button"
          title="Ajustes"
          data-testid="sidebar-settings"
          onClick={() => { nav('/settings'); onNavigate && onNavigate(); }}
          className={`gmail-folder ${onSettings ? 'gmail-folder-active' : ''}`}
          aria-current={onSettings ? 'page' : undefined}
          style={{ marginBottom: 0 }}
        >
          <SettingsIcon className="h-[18px] w-[18px] shrink-0" strokeWidth={onSettings ? 2.15 : 1.75} />
          <span className="od-side-text truncate flex-1 text-left">Ajustes</span>
        </button>
      </div>

      {/* ---------- Crear / renombrar carpeta ---------- */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              {dlg?.modo === 'crear'
                ? (dlg?.parent ? `Nueva subcarpeta en ${dlg.etiquetaPadre}` : 'Nueva carpeta')
                : 'Renombrar carpeta'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              autoFocus
              value={dlg?.nombre || ''}
              onChange={(e) => setDlg((x) => ({ ...x, nombre: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && guardarCarpeta()}
              placeholder="Clientes, Facturas, Proyecto…"
              className="h-10 rounded-lg"
              data-testid="folder-name-input"
            />
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>
              {dlg?.modo === 'crear'
                ? 'Se crea en tu buzón de verdad, así que la verás también en el móvil.'
                : 'Las subcarpetas y el correo se van con ella.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button onClick={guardarCarpeta} disabled={dlg?.busy || !(dlg?.nombre || '').trim()} data-testid="folder-save">
              {dlg?.busy ? 'Guardando…' : (dlg?.modo === 'crear' ? 'Crear' : 'Guardar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Eliminar carpeta ---------- */}
      <Dialog open={!!borrar} onOpenChange={(o) => !o && setBorrar(null)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle>Eliminar “{borrar?.label}”</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm" style={{ color: 'var(--text-2)' }}>
            <p>
              Se elimina la carpeta y sus subcarpetas. <strong>El correo no se
              borra</strong>: lo que haya dentro se pone a salvo antes de que la
              carpeta desaparezca, y te decimos dónde queda.
            </p>
            <p style={{ color: 'var(--text-3)' }}>
              Las reglas que enviaban correo aquí se desactivan.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBorrar(null)}>Cancelar</Button>
            <Button
              onClick={eliminarCarpeta}
              disabled={borrar?.busy}
              style={{ background: 'var(--danger)', color: '#fff' }}
              data-testid="folder-delete-confirm"
            >
              {borrar?.busy ? 'Eliminando…' : 'Eliminar carpeta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
